/** Каталоги: типы продукта, характеристики (с цветом), каналы планирования.
 * Порт секций tech-вкладки (стр. 8120-8175). Переименования каскадят на сервере. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import * as api from "@/api/orders-planning";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { extractErrorMessage } from "@/lib/api-errors";

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["orders", "refs"] });
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="mb-2 text-sm font-medium">{title}</div>
      {children}
    </div>
  );
}

export function CatalogsEditor({ canEdit }: { canEdit: boolean }): React.ReactElement {
  return (
    <div className="grid gap-3 lg:grid-cols-3">
      <ProductTypesSection canEdit={canEdit} />
      <CharacteristicsSection canEdit={canEdit} />
      <ChannelsSection canEdit={canEdit} />
    </div>
  );
}

function ProductTypesSection({ canEdit }: { canEdit: boolean }) {
  const invalidate = useInvalidate();
  const q = useQuery({ queryKey: ["orders", "refs", "product-types"], queryFn: api.listProductTypes });
  const [newName, setNewName] = useState("");
  const create = useMutation({
    mutationFn: api.createProductType,
    onSuccess: () => {
      invalidate();
      setNewName("");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось добавить")),
  });
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => api.updateProductType(id, { name }),
    onSuccess: invalidate,
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось переименовать")),
  });
  const del = useMutation({
    mutationFn: api.deleteProductType,
    onSuccess: invalidate,
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось удалить")),
  });

  return (
    <Section title="Типы продукта">
      <div className="space-y-1.5">
        {(q.data ?? []).map((t) => (
          <NameRow
            key={t.id}
            name={t.name}
            canEdit={canEdit}
            onRename={(name) => rename.mutate({ id: t.id, name })}
            onDelete={() => del.mutate(t.id)}
          />
        ))}
      </div>
      {canEdit && (
        <div className="mt-2 flex gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Новый тип"
            className="h-8"
          />
          <Button size="sm" disabled={!newName.trim()} onClick={() => create.mutate(newName.trim())}>
            Добавить
          </Button>
        </div>
      )}
    </Section>
  );
}

function CharacteristicsSection({ canEdit }: { canEdit: boolean }) {
  const invalidate = useInvalidate();
  const q = useQuery({
    queryKey: ["orders", "refs", "characteristics"],
    queryFn: api.listCharacteristics,
  });
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#5ec8f2");
  const create = useMutation({
    mutationFn: api.createCharacteristic,
    onSuccess: () => {
      invalidate();
      setNewName("");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось добавить")),
  });
  const upd = useMutation({
    mutationFn: ({ id, ...body }: { id: number; name?: string; color?: string }) =>
      api.updateCharacteristic(id, body),
    onSuccess: invalidate,
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось сохранить")),
  });
  const del = useMutation({
    mutationFn: api.deleteCharacteristic,
    onSuccess: invalidate,
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось удалить")),
  });

  return (
    <Section title="Характеристики и цвета строк">
      <div className="space-y-1.5">
        {(q.data ?? []).map((c) => (
          <NameRow
            key={c.id}
            name={c.name}
            color={c.color}
            canEdit={canEdit}
            onRename={(name) => upd.mutate({ id: c.id, name })}
            onColor={(color) => upd.mutate({ id: c.id, color })}
            onDelete={() => {
              if (window.confirm("У артикулов значение останется, но цвет пропадёт. Удалить?")) {
                del.mutate(c.id);
              }
            }}
          />
        ))}
      </div>
      {canEdit && (
        <div className="mt-2 flex items-center gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Новая характеристика"
            className="h-8"
          />
          <input
            type="color"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            className="size-8 cursor-pointer rounded border"
          />
          <Button
            size="sm"
            disabled={!newName.trim()}
            onClick={() => create.mutate({ name: newName.trim(), color: newColor })}
          >
            Добавить
          </Button>
        </div>
      )}
    </Section>
  );
}

const KIND_LABEL: Record<string, string> = { mp: "МП", wholesale: "Опт" };

function ChannelsSection({ canEdit }: { canEdit: boolean }) {
  const invalidate = useInvalidate();
  const q = useQuery({ queryKey: ["orders", "refs", "channels"], queryFn: api.listChannels });
  const [newName, setNewName] = useState("");
  const [newKind, setNewKind] = useState<"mp" | "wholesale">("mp");
  const create = useMutation({
    mutationFn: api.createChannel,
    onSuccess: () => {
      invalidate();
      setNewName("");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось добавить канал")),
  });
  const upd = useMutation({
    mutationFn: ({ id, ...body }: { id: number; name?: string; kind?: "mp" | "wholesale" }) =>
      api.updateChannel(id, body),
    onSuccess: invalidate,
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось сохранить")),
  });
  const del = useMutation({
    mutationFn: api.deleteChannel,
    onSuccess: invalidate,
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось удалить")),
  });

  return (
    <Section title="Каналы планирования">
      <div className="space-y-1.5">
        {(q.data ?? []).map((c) => (
          <div key={c.id} className="flex items-center gap-2 text-xs">
            <span className="w-20 shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">
              {c.code}
            </span>
            <EditableName
              name={c.name}
              canEdit={canEdit}
              onRename={(name) => upd.mutate({ id: c.id, name })}
            />
            <select
              className="h-7 rounded border bg-background px-1 text-xs"
              disabled={!canEdit}
              value={c.kind}
              onChange={(e) => upd.mutate({ id: c.id, kind: e.target.value as "mp" | "wholesale" })}
            >
              <option value="mp">{KIND_LABEL.mp}</option>
              <option value="wholesale">{KIND_LABEL.wholesale}</option>
            </select>
            {canEdit && (
              <button
                type="button"
                className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                onClick={() => del.mutate(c.id)}
              >
                <Trash2 className="size-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      {canEdit && (
        <div className="mt-2 flex items-center gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Новый канал"
            className="h-8"
          />
          <select
            className="h-8 rounded border bg-background px-1 text-xs"
            value={newKind}
            onChange={(e) => setNewKind(e.target.value as "mp" | "wholesale")}
          >
            <option value="mp">МП</option>
            <option value="wholesale">Опт</option>
          </select>
          <Button
            size="sm"
            disabled={!newName.trim()}
            onClick={() => create.mutate({ name: newName.trim(), kind: newKind })}
          >
            Добавить
          </Button>
        </div>
      )}
    </Section>
  );
}

function EditableName({
  name,
  canEdit,
  onRename,
}: {
  name: string;
  canEdit: boolean;
  onRename: (name: string) => void;
}) {
  const [value, setValue] = useState(name);
  React.useEffect(() => setValue(name), [name]);
  return (
    <input
      className="h-7 w-full rounded border bg-background px-1.5 text-xs focus:border-primary focus:outline-none"
      disabled={!canEdit}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        const v = value.trim();
        if (v && v !== name) onRename(v);
        else setValue(name);
      }}
    />
  );
}

function NameRow({
  name,
  color,
  canEdit,
  onRename,
  onColor,
  onDelete,
}: {
  name: string;
  color?: string;
  canEdit: boolean;
  onRename: (name: string) => void;
  onColor?: (color: string) => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <EditableName name={name} canEdit={canEdit} onRename={onRename} />
      {color !== undefined && (
        <input
          type="color"
          value={color}
          disabled={!canEdit}
          onChange={(e) => onColor?.(e.target.value)}
          className="size-7 shrink-0 cursor-pointer rounded border"
        />
      )}
      {canEdit && (
        <button
          type="button"
          className="shrink-0 text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
          onClick={onDelete}
        >
          <Trash2 className="size-3.5" />
        </button>
      )}
    </div>
  );
}
